<template>
  <div class="tl-card">
    <header class="tl-header">
      <div>
        <h5 class="tl-title">Locais mais visitados</h5>
        <p class="tl-subtitle">
          Ranking por visitantes únicos. Atualiza em tempo real.
        </p>
      </div>
    </header>

    <div v-if="!locations.length" class="tl-empty">
      <i class="bx bx-map tl-empty-icon"></i>
      <p>Sem visitas registradas no período</p>
      <small>Os locais aparecem aqui conforme as pessoas fazem check-in</small>
    </div>

    <transition-group v-else tag="div" class="tl-list" name="tl-row">
      <div
        v-for="(loc, idx) in visibleLocations"
        :key="loc.tag_id"
        class="tl-item"
        :class="[
          rankClass(loc.rank),
          { 'tl-item-fade': hasMore && idx === visibleLocations.length - 1 }
        ]"
        @click="$emit('select-location', loc)"
      >
        <div class="tl-row">
          <span class="tl-rank">{{ formatRank(loc.rank) }}</span>

          <div class="tl-row-body">
            <div class="tl-row-top">
              <span class="tl-name" :title="loc.tag_name">{{ loc.tag_name }}</span>
              <span class="tl-row-visits">{{ loc.unique_visits }}</span>
            </div>
            <div class="tl-row-foot">
              <span class="tl-pct">{{ loc.pct_of_total }}% do total</span>
              <span v-if="loc.peak_label" class="tl-peak">
                pico em <strong>{{ loc.peak_label }}</strong>
              </span>
            </div>
          </div>
        </div>

        <!-- Histograma full-width abaixo da row.
             Substitui a barra horizontal de "volume relativo" — comunica
             tanto a magnitude (altura das barras) quanto o tempo (eixo X). -->
        <div
          v-if="loc.histogram && loc.histogram.peak_count > 0"
          class="tl-histogram"
          :title="histogramTooltip(loc.histogram)"
        >
          <div class="tl-histogram-bars">
            <span
              v-for="b in loc.histogram.buckets"
              :key="b.idx"
              class="tl-histogram-bar"
              :class="{ 'tl-histogram-bar-peak': b.idx === loc.histogram.peak_idx }"
              :style="{ height: barHeight(b.count, loc.histogram.peak_count) + '%' }"
              :title="`${b.label} · ${b.count} leituras`"
            ></span>
          </div>
          <div class="tl-histogram-axis">
            <span>{{ loc.histogram.buckets[0].label }}</span>
            <span class="tl-histogram-axis-mid">distribuição no período</span>
            <span>{{ loc.histogram.buckets[loc.histogram.buckets.length - 1].label }}</span>
          </div>
        </div>
      </div>
    </transition-group>

    <button
      v-if="hasMore"
      type="button"
      class="tl-show-more"
      @click="showMore"
    >
      <i class="bx bx-chevron-down"></i>
      Ver mais {{ Math.min(STEP, remaining) }}
      <span class="tl-show-more-of">de {{ remaining }} restantes</span>
    </button>
  </div>
</template>

<script>
const INITIAL_VISIBLE = 5
const STEP = 5

export default {
  name: 'top-locations',
  props: {
    locations: { type: Array, default: () => [] }
  },
  data: () => ({
    visibleCount: INITIAL_VISIBLE,
    STEP
  }),
  watch: {
    /**
     * Reseta quando o array de localizações muda de tamanho/conjunto —
     * evita ficar "preso" expandido quando o usuário muda filtro do dash.
     * Não reseta quando só os números mudam (polling do mesmo conjunto).
     */
    locations (newVal, oldVal) {
      if (!oldVal || newVal.length !== oldVal.length) {
        this.visibleCount = INITIAL_VISIBLE
      }
    }
  },
  computed: {
    visibleLocations () {
      return this.locations.slice(0, this.visibleCount)
    },
    hasMore () {
      return this.locations.length > this.visibleCount
    },
    remaining () {
      return Math.max(0, this.locations.length - this.visibleCount)
    },
    maxVisits () {
      if (!this.locations.length) return 1
      return this.locations.reduce((m, l) => Math.max(m, l.unique_visits || 0), 1)
    }
  },
  methods: {
    relativePct (loc) {
      const v = loc.unique_visits || 0
      return Math.max(3, Math.min(100, Math.round((v / this.maxVisits) * 100)))
    },
    rankClass (rank) {
      if (rank === 1) return 'tl-row-first'
      if (rank === 2 || rank === 3) return 'tl-row-podium'
      return ''
    },
    formatRank (rank) {
      return `${rank}º`
    },
    barHeight (count, peak) {
      if (!peak) return 6
      if (count === 0) return 6
      const pct = (count / peak) * 100
      return Math.max(15, Math.min(100, pct))
    },
    histogramTooltip (h) {
      const peak = h.buckets[h.peak_idx]
      if (!peak) return ''
      return `Pico em ${peak.label} (${peak.count} leituras)`
    },
    showMore () {
      this.visibleCount += STEP
    }
  }
}
</script>

<style lang="scss" scoped>
.tl-card {
  background: #fff;
  border: 1px solid #eaecf2;
  border-radius: 5px;
  padding: 22px 24px;
  box-shadow: 0 1px 3px rgba(26, 29, 38, 0.04);
}

.tl-header {
  margin-bottom: 18px;
}

.tl-title {
  font-size: 14px;
  font-weight: 700;
  color: #1a1d26;
  margin: 0 0 4px 0;
}

.tl-subtitle {
  font-size: 12px;
  color: #8b90a5;
  margin: 0;
}

.tl-empty {
  padding: 40px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #8b90a5;
  text-align: center;

  .tl-empty-icon { font-size: 42px; color: #d0d5dd; margin-bottom: 8px; }
  p { font-size: 13px; font-weight: 600; margin: 0 0 4px 0; color: #4a5068; }
  small { font-size: 12px; }
}

.tl-list {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.tl-item {
  padding: 14px 4px;
  cursor: pointer;
  transition: background 0.15s ease;
  border-radius: 5px;
  position: relative;

  & + .tl-item { border-top: 1px solid #f0f1f6; }

  &:hover {
    background: #fafbfc;
  }

  /* Fade gradient no último item visível quando há mais ocultos.
     Comunica visualmente que existe conteúdo abaixo. */
  &.tl-item-fade::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: -1px;
    height: 50%;
    background: linear-gradient(to bottom, transparent, #fff 90%);
    pointer-events: none;
    z-index: 1;
  }

  /* Destaque pro 1º lugar — só na cor do número e do nome */
  &.tl-row-first {
    .tl-rank {
      background: rgba(85, 110, 230, 0.12);
      color: #556ee6;
    }
    .tl-name { font-weight: 700; }
    .tl-histogram-bar-peak { background: #556ee6; }
  }

  &.tl-row-podium {
    .tl-rank {
      background: rgba(85, 110, 230, 0.06);
      color: #556ee6;
    }
  }
}

.tl-row {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 0 0 8px;
}

.tl-rank {
  width: 36px;
  height: 36px;
  display: grid;
  place-items: center;
  background: #f0f1f6;
  color: #4a5068;
  font-size: 13px;
  font-weight: 700;
  border-radius: 8px;
  flex-shrink: 0;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.02em;
  transition: all 0.2s ease;
}

.tl-row-body {
  flex: 1;
  min-width: 0;
}

.tl-row-top {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 10px;
  margin-bottom: 4px;
}

.tl-name {
  font-size: 14px;
  font-weight: 600;
  color: #1a1d26;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.tl-row-visits {
  font-size: 18px;
  font-weight: 800;
  color: #1a1d26;
  font-variant-numeric: tabular-nums;
  flex-shrink: 0;
  letter-spacing: -0.02em;
}

.tl-row-foot {
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  color: #8b90a5;
  font-weight: 500;
}

.tl-pct {
  font-weight: 600;
}

.tl-peak {
  color: #8b90a5;

  strong {
    color: #4a5068;
    font-weight: 600;
  }
}

/* Histograma full-width — substitui a antiga barra horizontal de "volume
   relativo". Comunica magnitude (altura) e tempo (eixo X) numa visualização
   só. Bar do pico em sólido, demais em pastel. */
.tl-histogram {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-left: 50px; /* alinha com o body, fora da coluna do rank */
}

.tl-histogram-bars {
  display: flex;
  align-items: flex-end;
  gap: 2px;
  height: 32px;
}

.tl-histogram-bar {
  flex: 1;
  min-width: 4px;
  background: rgba(176, 181, 200, 0.3);
  border-radius: 2px 2px 0 0;
  transition: background 0.2s ease, height 0.6s cubic-bezier(0.4, 0, 0.2, 1);

  &.tl-histogram-bar-peak {
    background: #8b90a5;
  }
}

.tl-histogram-axis {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 9px;
  font-weight: 600;
  color: #b0b5c8;
  letter-spacing: 0.02em;
}

.tl-histogram-axis-mid {
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #d0d5dd;
}

/* Animação do auto-rerank — quando uma row sobe/desce no ranking */
.tl-row-move {
  transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1);
}

.tl-row-enter-active,
.tl-row-leave-active {
  transition: opacity 0.4s ease, transform 0.4s ease;
}

.tl-row-enter {
  opacity: 0;
  transform: translateX(-20px);
}

.tl-row-leave-to {
  opacity: 0;
  transform: translateX(20px);
}

.tl-row-leave-active {
  position: absolute;
  width: calc(100% - 8px);
}

/* Botão "Ver mais" — abaixo da lista, captura a continuidade do fade */
.tl-show-more {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  margin-top: 4px;
  padding: 10px 14px;
  background: #fafbfc;
  border: 1px solid #eaecf2;
  border-radius: 8px;
  color: #556ee6;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;

  i {
    font-size: 18px;
    transition: transform 0.2s ease;
  }

  &:hover {
    background: #fff;
    border-color: #c5caf5;
    box-shadow: 0 2px 8px rgba(85, 110, 230, 0.08);

    i { transform: translateY(2px); }
  }
}

.tl-show-more-of {
  font-size: 11px;
  color: #8b90a5;
  font-weight: 500;
  margin-left: 4px;
}

@media (max-width: 540px) {
  .tl-histogram { padding-left: 0; }
  .tl-histogram-axis-mid { display: none; }
}
</style>
